/**
 * Progress page layout.
 *
 * Stripe-inspired visual language: fixed header, scrollable body capped at
 * max-w-7xl, indigo/violet palette, and `Card` from `@ai-tutor/ui` for every
 * surface. The four sections used to hand-write that card recipe ten times
 * between them — this comment used to spell the class string out, which is how
 * a house style gets documented in prose instead of enforced in code.
 *
 * (This used to say "mirrors `AnalyticsLayout`". That component was the root of
 * an unreachable 1.7k-line `features/analytics/` tree — flagged dead in
 * August, relocated rather than deleted by the XON-102 restructure, and removed
 * outright in the follow-up. The only live file under it was this page's
 * skeleton, since dissolved into the per-section skeletons described below.)
 *
 * Data shape is our `MyProgressResponse` from `useMyProgress` - calendar-
 * bounded periods (This week / This month / This year), with past-year
 * navigation surfaced as small year chips below the header. The window is in
 * the URL (`useProgressView`), so a reload keeps it.
 *
 * Below `md` the page is laid out for a phone: one scroll, a sticky period bar
 * that also holds the past years (`ProgressPhoneBar`), the numbers before the
 * insights, and two insights before "Show more".
 *
 * There is no page-level loading state. Each section renders its own skeleton
 * (`StreaksSection`, `MindspaceSection`, `MyLessonSection`, `InsightsSection`),
 * so the placeholder always lives beside the markup it stands in for and cannot
 * drift from it. See the comment in the body for the bug that motivated it.
 */

import type { ReactNode } from 'react';
import { useMyProgress } from '@/hooks/mindspace/useMyProgress';
import { useInsights } from '@/hooks/mindspace/useInsights';
import {
  HISTORY_FLOOR_YEAR,
  useProgressView,
} from '@/hooks/mindspace/useProgressView';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import type { ProgressPeriod } from '@/lib/api-client';
import { InsightsSection } from './InsightsSection';
import { StreaksSection } from './StreaksSection';
import { MindspaceSection } from './MindspaceSection';
import { MyLessonSection } from './MyLessonSection';
import { PeriodToggle } from './PeriodToggle';
import { ProgressPhoneBar } from './ProgressPhoneBar';
import { SectionLabel } from './SectionLabel';

// Most consecutive past years to surface as quick-jump chips.
const MAX_HISTORY_CHIPS = 5;

// How many insights a phone shows before "Show N more". Five full-width cards
// were a screen and a half of prose above the first number.
const PHONE_INSIGHTS = 2;

/** The archive years on offer, newest first: floor ≤ year < the current one. */
function historyYears(currentYear: number): number[] {
  return Array.from(
    { length: Math.min(currentYear - HISTORY_FLOOR_YEAR, MAX_HISTORY_CHIPS) },
    (_, i) => currentYear - 1 - i
  ).filter(y => y >= HISTORY_FLOOR_YEAR);
}

export function ProgressLayout() {
  const { period, pastYear, setPeriod, setPastYear } = useProgressView();
  // Below `md` the page is one scroll with a sticky period bar, and the
  // sections reorder. Mounted per branch rather than hidden with CSS so the
  // phone never renders the desktop header's chips and toggle as well.
  const phone = useMediaQuery('(max-width: 767.98px)');

  // Past-year views always render the full year (weekly buckets) regardless
  // of the period toggle, so both the REQUEST and the heatmap's granularity
  // have to follow the *effective* window rather than the toggle state.
  //
  // The request half of that was missing: `useMyProgress(period, pastYear)`
  // asked for `this_week` of a year that has already ended, so the archive
  // view came back with seven daily buckets - the current week's dates - while
  // the heatmap below labelled them W1..W7 as though they were weeks of 2025.
  // Only the labelling side had been fixed, which is why the comment described
  // a behaviour the code did not have.
  const effectivePeriod: ProgressPeriod = pastYear ? 'this_year' : period;

  const { data, isLoading, isError } = useMyProgress(effectivePeriod, pastYear);

  // Insights are live, present-tense coaching - they don't make sense for a
  // historical archive view, so we only fetch/render them for the current
  // year. LLM-backed in API mode, rule-based in mock mode, empty in empty
  // mode. See `useInsights` for the branch logic.
  const showInsights = !pastYear;
  const insightsQuery = useInsights({
    period,
    progress: data,
    enabled: showInsights,
  });

  // There is no page-level skeleton. Every section renders its own loading
  // state next to the markup it mirrors, and the chrome around them - title,
  // subtitle fallback, period toggle, section labels - is all known before the
  // query resolves, so it simply renders. Shimmering any of it would fake a
  // wait for data we already have.
  //
  // This replaced a `ProgressPageSkeleton` that mirrored all four sections from
  // one file and drifted from them: it drew an Insights grid unconditionally
  // while this component hides Insights for a past-year archive, so jumping to
  // an archive year showed six cards that then disappeared.
  const insights = showInsights && (
    <InsightsSection
      insights={insightsQuery.data ?? []}
      isLoading={isLoading || insightsQuery.isLoading}
      // A failed REFETCH keeps `data`, so only a read with nothing cached is a
      // failure here.
      loadFailed={insightsQuery.isError && !insightsQuery.data}
      onRetry={() => void insightsQuery.refetch()}
      initialVisible={phone ? PHONE_INSIGHTS : undefined}
    />
  );
  const sections = (
    <ProgressSections
      failed={isError}
      // A phone leads with the numbers: the streak and the four stats are the
      // answer to "how am I doing", and the insights are commentary on them.
      // Beside `md` the insights sit first as a grid you take in at a glance.
      before={phone ? undefined : insights}
      after={phone ? insights : undefined}
      overview={
        <StreaksSection
          data={data}
          isLoading={isLoading}
          period={effectivePeriod}
        />
      }
      retention={<MindspaceSection data={data} isLoading={isLoading} />}
      activity={<MyLessonSection data={data} isLoading={isLoading} />}
    />
  );
  const title = (
    <div>
      <h1 className='text-3xl font-bold text-foreground'>Progress</h1>
      <p className='mt-1 text-muted-foreground'>
        {data?.period_label ?? 'Your learning overview'}
      </p>
    </div>
  );

  if (phone) {
    return (
      // One scroll for the whole page: the title scrolls away and only the
      // period bar sticks. Sticky is positioned, so the bar paints over the
      // page lattice (a positioned layer) as the content slides under it.
      <div className='min-h-0 flex-1 overflow-y-auto'>
        <div className='px-4 pb-3 pt-6'>{title}</div>
        <div className='sticky top-0 z-10 bg-background/90 px-4 py-2 backdrop-blur-sm'>
          <ProgressPhoneBar
            period={period}
            onChange={setPeriod}
            pastYear={pastYear}
            onPastYear={setPastYear}
            years={historyYears(data?.year ?? new Date().getFullYear())}
          />
        </div>
        <div className='px-4 pb-8 pt-4'>{sections}</div>
      </div>
    );
  }

  const years = !pastYear && data ? historyYears(data.year) : [];
  return (
    <>
      {/* Fixed header */}
      <div className='mx-auto w-full max-w-7xl flex-shrink-0 px-4 pb-6 pt-8 sm:px-6 lg:px-8'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
          {title}
          <PeriodToggle
            period={period}
            onChange={setPeriod}
            viewingPastYear={pastYear}
            onClearPastYear={() => setPastYear(undefined)}
          />
        </div>

        {/* Past-year history strip - only when there's history to look at. */}
        {years.length > 0 && (
          <div className='mt-3 flex items-center gap-2 text-xs text-muted-foreground'>
            <span>History:</span>
            {years.map(y => (
              <button
                key={y}
                onClick={() => setPastYear(y)}
                className='rounded-md border border-border px-2 py-0.5 hover:bg-accent'
              >
                {y}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className='min-h-0 flex-1 overflow-y-auto'>
        <div className='mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8'>
          {sections}
        </div>
      </div>
    </>
  );
}

interface ProgressSectionsProps {
  failed: boolean;
  before?: ReactNode;
  overview: ReactNode;
  after?: ReactNode;
  retention: ReactNode;
  activity: ReactNode;
}

/** The page body, in one order or the other (see `ProgressLayout`). */
function ProgressSections({
  failed,
  before,
  overview,
  after,
  retention,
  activity,
}: ProgressSectionsProps) {
  return (
    <div className='space-y-8'>
      {failed && (
        <div className='rounded-xl border border-amber-200 bg-amber-50/80 px-5 py-4 text-sm text-amber-800 backdrop-blur-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200'>
          Couldn't load your progress right now. Try refreshing in a moment.
        </div>
      )}

      {before}

      {/* ── Overview ── streaks + aggregate metric cards */}
      <div className='space-y-3'>{overview}</div>

      {after}

      {/* ── Retention ── Mindspace */}
      <div className='space-y-3'>
        <SectionLabel>Retention</SectionLabel>
        {retention}
      </div>

      {/* ── Activity ── grouped by section, includes Mindspace */}
      <div className='space-y-3'>
        <SectionLabel>Activity</SectionLabel>
        {activity}
      </div>
    </div>
  );
}
